import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { articlesAPI, categoriesAPI } from '../utils/api';
import { Plus, Edit, Trash2, Eye, FileText, Clock, Upload, Download, CheckCircle, XCircle, Users, UserPen, Layers, Tag } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

export const AdminDashboard = () => {
  const { user, loading: authLoading, hasPermission } = useAuth();
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState({ total: 0, published: 0, drafts: 0, scheduled: 0 });
  const [loading, setLoading] = useState(true);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/admin/login');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const [articlesRes, categoriesRes, allArticlesRes] = await Promise.all([
        articlesAPI.getAll({ limit: 100 }),
        categoriesAPI.getAll(),
        articlesAPI.getAll({ is_published: null, limit: 1000 })
      ]);

      setArticles(articlesRes.data);
      setCategories(categoriesRes.data);
      
      const allArticles = allArticlesRes.data;
      setStats({
        total: allArticles.length,
        published: allArticles.filter(a => a.is_published || a.status === 'published').length,
        drafts: allArticles.filter(a => !a.is_published && a.status !== 'scheduled').length,
        scheduled: allArticles.filter(a => a.status === 'scheduled').length
      });
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this article?')) return;

    try {
      await articlesAPI.delete(id);
      toast.success('Article deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete article');
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-brand-red mx-auto mb-4"></div>
          <p className="text-gray-600 font-inter">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-playfair font-bold text-brand-green mb-2">Admin Dashboard</h1>
            <p className="text-gray-600 font-inter">Welcome back, {user.name}!</p>
          </div>
          <div className="flex gap-4">
            {user.role === 'super_admin' && (
              <Link
                to="/admin/users"
                className="inline-flex items-center gap-2 bg-purple-600 text-white px-6 py-3 font-bold hover:bg-purple-700 transition-colors uppercase tracking-wide rounded-lg"
                data-testid="manage-users-button"
              >
                <Users className="w-5 h-5" />
                Users
              </Link>
            )}
            {hasPermission('manage_authors') && (
              <Link
                to="/admin/authors"
                className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 font-bold hover:bg-blue-700 transition-colors uppercase tracking-wide rounded-lg"
                data-testid="manage-authors-button"
              >
                <UserPen className="w-5 h-5" />
                Authors
              </Link>
            )}
            {hasPermission('manage_pseo') && (
              <Link
                to="/admin/categories"
                className="inline-flex items-center gap-2 bg-amber-600 text-white px-6 py-3 font-bold hover:bg-amber-700 transition-colors uppercase tracking-wide rounded-lg"
                data-testid="manage-categories-button"
              >
                <Tag className="w-5 h-5" />
                Categories
              </Link>
            )}
            {hasPermission('manage_pseo') && (
              <Link
                to="/admin/brand-categories"
                className="inline-flex items-center gap-2 bg-orange-600 text-white px-6 py-3 font-bold hover:bg-orange-700 transition-colors uppercase tracking-wide rounded-lg"
                data-testid="manage-brand-categories-button"
              >
                <Tag className="w-5 h-5" />
                Brand Categories
              </Link>
            )}
            {hasPermission('manage_pseo') && (
              <Link
                to="/admin/pseo"
                className="inline-flex items-center gap-2 bg-brand-green text-white px-6 py-3 font-bold hover:bg-brand-green/90 transition-colors uppercase tracking-wide rounded-lg"
                data-testid="manage-pseo-button"
              >
                <Layers className="w-5 h-5" />
                pSEO
              </Link>
            )}
            {hasPermission('create_articles') && (
              <Link
                to="/admin/articles/new"
                className="inline-flex items-center gap-2 bg-brand-red text-white px-6 py-3 font-bold hover:bg-[#D62828] transition-colors uppercase tracking-wide rounded-lg"
                data-testid="create-article-button"
              >
                <Plus className="w-5 h-5" />
                New Article
              </Link>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
          <div className="bg-white border-2 border-gray-900 p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]" data-testid="stats-total">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 uppercase tracking-widest mb-2">Total Articles</p>
                <p className="text-4xl font-playfair font-bold text-brand-green">{stats.total}</p>
              </div>
              <FileText className="w-12 h-12 text-brand-green opacity-20" />
            </div>
          </div>

          <div className="bg-white border-2 border-gray-900 p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]" data-testid="stats-published">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 uppercase tracking-widest mb-2">Published</p>
                <p className="text-4xl font-playfair font-bold text-brand-red">{stats.published}</p>
              </div>
              <Eye className="w-12 h-12 text-brand-red opacity-20" />
            </div>
          </div>

          <div className="bg-white border-2 border-gray-900 p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]" data-testid="stats-scheduled">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 uppercase tracking-widest mb-2">Scheduled</p>
                <p className="text-4xl font-playfair font-bold text-blue-500">{stats.scheduled}</p>
              </div>
              <Clock className="w-12 h-12 text-blue-500 opacity-20" />
            </div>
          </div>

          <div className="bg-white border-2 border-gray-900 p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]" data-testid="stats-drafts">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 uppercase tracking-widest mb-2">Drafts</p>
                <p className="text-4xl font-playfair font-bold text-gray-500">{stats.drafts}</p>
              </div>
              <Edit className="w-12 h-12 text-gray-500 opacity-20" />
            </div>
          </div>
        </div>

        {/* Bulk Import/Export Section */}
        <div className="bg-white border-2 border-gray-900 p-6 mb-8 rounded-lg">
          <h2 className="text-xl font-playfair font-bold text-brand-green mb-4">Bulk Import/Export</h2>
          <div className="flex flex-wrap gap-4 items-center">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.json"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                
                setImporting(true);
                setImportResult(null);
                
                const formData = new FormData();
                formData.append('file', file);
                
                try {
                  const response = await articlesAPI.bulkImport(formData);
                  setImportResult(response.data);
                  toast.success(`Imported ${response.data.success} articles`);
                  fetchData();
                } catch (error) {
                  toast.error(error.response?.data?.detail || 'Import failed');
                  setImportResult({ success: 0, failed: 1, errors: [{ error: error.message }] });
                } finally {
                  setImporting(false);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }
              }}
              className="hidden"
              data-testid="bulk-import-input"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
              className="inline-flex items-center gap-2 bg-brand-green text-white px-4 py-2 rounded-lg font-semibold hover:bg-brand-green/90 transition-colors disabled:opacity-50"
              data-testid="bulk-import-button"
            >
              <Upload className="w-4 h-4" />
              {importing ? 'Importing...' : 'Import CSV/JSON'}
            </button>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/articles/export?format=csv`}
              className="inline-flex items-center gap-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
              data-testid="export-csv-button"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </a>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/articles/export?format=json`}
              className="inline-flex items-center gap-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
              data-testid="export-json-button"
            >
              <Download className="w-4 h-4" />
              Export JSON
            </a>
          </div>
          
          {importResult && (
            <div className={`mt-4 p-4 rounded-lg ${importResult.failed > 0 ? 'bg-yellow-50 border border-yellow-200' : 'bg-green-50 border border-green-200'}`}>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="font-semibold text-green-700">{importResult.success} imported</span>
                </div>
                {importResult.failed > 0 && (
                  <div className="flex items-center gap-2">
                    <XCircle className="w-5 h-5 text-red-500" />
                    <span className="font-semibold text-red-700">{importResult.failed} failed</span>
                  </div>
                )}
              </div>
              {importResult.errors?.length > 0 && (
                <div className="mt-2 text-sm text-gray-600">
                  <p className="font-semibold mb-1">Errors:</p>
                  <ul className="list-disc list-inside max-h-32 overflow-y-auto">
                    {importResult.errors.slice(0, 10).map((err, idx) => (
                      <li key={idx}>{err.title || 'Row ' + err.row}: {err.error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="bg-white border-2 border-gray-900">
          <div className="p-6 border-b-2 border-gray-900">
            <h2 className="text-2xl font-playfair font-bold text-brand-green">Recent Articles</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="articles-table">
              <thead className="bg-gray-50 border-b-2 border-gray-900">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-inter font-bold uppercase tracking-wider text-gray-700">Title</th>
                  <th className="px-6 py-4 text-left text-xs font-inter font-bold uppercase tracking-wider text-gray-700">Category</th>
                  <th className="px-6 py-4 text-left text-xs font-inter font-bold uppercase tracking-wider text-gray-700">Status</th>
                  <th className="px-6 py-4 text-left text-xs font-inter font-bold uppercase tracking-wider text-gray-700">Date</th>
                  <th className="px-6 py-4 text-left text-xs font-inter font-bold uppercase tracking-wider text-gray-700">Views</th>
                  <th className="px-6 py-4 text-right text-xs font-inter font-bold uppercase tracking-wider text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {articles.map((article) => (
                  <tr key={article.id} className="hover:bg-gray-50" data-testid={`article-row-${article.id}`}>
                    <td className="px-6 py-4">
                      <div className="font-inter font-medium text-brand-green">{article.title}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-600">{article.category_name}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-block px-3 py-1 text-xs font-bold uppercase ${
                        article.is_published ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {article.is_published ? 'Published' : 'Draft'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-600">
                        {format(new Date(article.created_at), 'MMM dd, yyyy')}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-600">{article.views}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <Link
                          to={`/article/${article.slug}`}
                          className="p-2 text-gray-600 hover:text-brand-green transition-colors"
                          data-testid={`view-article-${article.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Link>
                        <Link
                          to={`/admin/articles/edit/${article.id}`}
                          className="p-2 text-gray-600 hover:text-brand-green transition-colors"
                          data-testid={`edit-article-${article.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Link>
                        <button
                          onClick={() => handleDelete(article.id)}
                          className="p-2 text-gray-600 hover:text-brand-red transition-colors"
                          data-testid={`delete-article-${article.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};